import {gql} from '@apollo/client';

import {assertNotNullAndReturn} from 'Asserts';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import type {
  ProcedureDroppedEdgesQuery,
  ProcedureDroppedEdgesQueryVariables,
  ProcedureDroppedEdgesQuery_procedureFlowchartConfigVersion_ProcedureFlowchartConfigVersionNode_edges_ProcedureEdgeNode as DroppedEdgesEdge,
  ProcedureDroppedEdgesQuery_procedureFlowchartConfigVersion_ProcedureFlowchartConfigVersionNode_migratedFromFlowchartVersion_ProcedureFlowchartVersionNode_nodes_ProcedureMethodDefinitionVersionNode as DroppedEdgesNode,
} from './__generated/ProcedureDroppedEdgesQuery';

const PROCEDURE_DROPPED_EDGES_QUERY = gql`
  query ProcedureDroppedEdgesQuery($flowchartConfigVersionId: StrictString!)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    procedureFlowchartConfigVersion(id: $flowchartConfigVersionId) {
      ... on ProcedureFlowchartConfigVersionNode {
        id
        edges {
          type
          sourceId
          targetId
        }
        migratedFromFlowchartVersion {
          ... on ProcedureFlowchartVersionNode {
            id
            nodes {
              id
              name
              materialInputSettings {
                version {
                  id
                  name
                }
              }
              materialOutputSettings {
                version {
                  id
                  name
                }
              }
            }
            edges {
              type
              sourceId
              targetId
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Returns human-readable labels for material edges that were present in the legacy
 * flowchart but missing from the PoW flowchart (i.e. dropped during migration).
 * Only MATERIAL_EDGE edges can be dropped during migration.
 *
 * @internal
 */
export function getDroppedEdgeLabels(
  legacyNodes: Array<DroppedEdgesNode>,
  legacyEdges: Array<DroppedEdgesEdge>,
  powEdges: Array<DroppedEdgesEdge>
): Array<string> {
  const powEdgeKeys = new Set(powEdges.map((e) => `${e.sourceId}:${e.targetId}`));
  const droppedEdges = legacyEdges.filter((e) => !powEdgeKeys.has(`${e.sourceId}:${e.targetId}`));

  if (droppedEdges.length === 0) {
    return [];
  }

  const materialVersionIdToLabel: Record<string, string> = {};
  for (const node of legacyNodes) {
    for (const setting of node.materialOutputSettings) {
      materialVersionIdToLabel[setting.version.id] =
        `Unit Op: ${node.name} - Material Output: ${setting.version.name}`;
    }
    for (const setting of node.materialInputSettings) {
      materialVersionIdToLabel[setting.version.id] =
        `Unit Op: ${node.name} - Material Input: ${setting.version.name}`;
    }
  }

  return droppedEdges.map((edge) => {
    const sourceLabel = assertNotNullAndReturn(materialVersionIdToLabel[edge.sourceId]);
    const targetLabel = assertNotNullAndReturn(materialVersionIdToLabel[edge.targetId]);
    return `${sourceLabel} → ${targetLabel}`;
  });
}

/**
 * Formats dropped edge labels into a user-facing banner message, or returns
 * undefined if there are no dropped edges.
 *
 * @internal
 */
export function getDroppedEdgesMessage(
  legacyNodes: Array<DroppedEdgesNode>,
  legacyEdges: Array<DroppedEdgesEdge>,
  powEdges: Array<DroppedEdgesEdge>
): string | null {
  const labels = getDroppedEdgeLabels(legacyNodes, legacyEdges, powEdges);
  if (labels.length === 0) {
    return null;
  }
  return `The following material mappings couldn't be carried over after a recent update and were removed:\n${labels.map((d) => `• ${d}`).join('\n')}`;
}

/**
 * Hook that detects if a specific migrated PoW procedure version had edges dropped during migration.
 * Returns a descriptive warning message listing the dropped edges, or undefined if
 * no edges were dropped or the version was not migrated.
 */
export default function useDroppedEdgesBanner(flowchartConfigVersionId: string | undefined): string | null {
  const result = useSkippableBenchlingQuery<ProcedureDroppedEdgesQuery, ProcedureDroppedEdgesQueryVariables>(
    PROCEDURE_DROPPED_EDGES_QUERY,
    {
      variables: {flowchartConfigVersionId: flowchartConfigVersionId ?? ''},
      skip: flowchartConfigVersionId == null,
    }
  );

  if (result.type !== QueryResultType.SUCCESS) {
    return null;
  }

  const configVersion = result.data.procedureFlowchartConfigVersion;
  if (isInaccessible(configVersion)) {
    return null;
  }

  const legacyVersion = configVersion.migratedFromFlowchartVersion;
  if (legacyVersion == null || isInaccessible(legacyVersion)) {
    return null;
  }

  return getDroppedEdgesMessage(legacyVersion.nodes, legacyVersion.edges, configVersion.edges);
}
